import { useMemo, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { formatCedi } from "../lib/api";
import {
  products as seedProducts, productCategories, salesTrend, topCustomers,
} from "../lib/mockData";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { AlertTriangle, Barcode, DollarSign, Package, Search, ShoppingCart, TrendingUp } from "lucide-react";
import BeforeAfter from "../components/common/BeforeAfter";
import ConsultationCTA from "../components/common/ConsultationCTA";

const CHART_COLORS = ["#005BBB", "#00C853", "#7C3AED", "#F59E0B", "#0EA5E9"];

const DemoHeader = () => (
  <div className="mb-8">
    <div className="text-xs font-semibold uppercase tracking-widest text-primary">Demo 5</div>
    <h1 className="mt-1 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">Inventory & Sales Dashboard</h1>
    <p className="mt-3 text-muted-foreground text-lg max-w-3xl">
      A retail-ready view of your stock, sales and profitability — with low-stock alerts and one-click purchase orders.
    </p>
  </div>
);

const KPI = ({ icon: Icon, label, value, delta }) => (
  <Card className="glass"><CardContent className="p-5">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="w-4 h-4"/></div>
    </div>
    <div className="mt-2 font-display text-2xl font-bold">{value}</div>
    {delta && <div className="text-xs text-accent mt-1">▲ {delta}</div>}
  </CardContent></Card>
);

export default function Demo5Inventory() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [products] = useState(seedProducts);

  const filtered = useMemo(() => products.filter((p) => {
    const matchesQ = !q || [p.name, p.sku, p.barcode].some((v) => v.toLowerCase().includes(q.toLowerCase()));
    const matchesC = cat === "all" || p.category === cat;
    return matchesQ && matchesC;
  }), [q, cat, products]);

  const totalRevenue = salesTrend.reduce((s, r) => s + r.revenue, 0);
  const totalOrders = salesTrend.reduce((s, r) => s + r.orders, 0);
  const lowStock = products.filter((p) => p.stock < p.reorder);
  const catData = productCategories.map((c) => ({
    name: c, value: products.filter((p) => p.category === c).reduce((s, p) => s + p.stock * p.price, 0),
  })).filter((r) => r.value > 0);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <DemoHeader/>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
        <KPI icon={DollarSign} label="Revenue (6mo)" value={formatCedi(totalRevenue)} delta="18.6%"/>
        <KPI icon={ShoppingCart} label="Orders (6mo)" value={totalOrders} delta="12.3%"/>
        <KPI icon={Package} label="Products" value={products.length} delta="2 new this month"/>
        <KPI icon={AlertTriangle} label="Low stock" value={lowStock.length} delta={lowStock.length ? "Action needed" : "All good"}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="glass lg:col-span-2"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-primary">Revenue trend</div>
              <div className="font-display text-lg font-semibold">Monthly performance</div>
            </div>
            <Badge variant="outline"><TrendingUp className="w-3 h-3 mr-1"/> +18.6%</Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={salesTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="month" tickLine={false} stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12}/>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
                <Line type="monotone" dataKey="revenue" stroke="#005BBB" strokeWidth={3} dot={{ r: 4 }}/>
                <Line type="monotone" dataKey="orders" stroke="#00C853" strokeWidth={2} strokeDasharray="4 4"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>

        <Card className="glass"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-primary">By category</div>
          <div className="font-display text-lg font-semibold mb-2">Stock value</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={catData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  paddingAngle={2}
                  stroke="hsl(var(--card))"
                  strokeWidth={2}
                >
                  {catData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={(v) => formatCedi(v)}/>
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>
      </div>

      <Card className="glass mb-6"><CardContent className="p-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary">Inventory</div>
            <div className="font-display text-lg font-semibold">All products</div>
          </div>
          <div className="sm:ml-auto flex flex-wrap gap-2 w-full sm:w-auto">
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
              <Input className="pl-9 h-9 w-full sm:w-64" placeholder="Search name, SKU or barcode" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search"/>
            </div>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="w-full sm:w-40 h-9" data-testid="inv-cat-filter"><SelectValue placeholder="Category"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {productCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="grid grid-cols-12 text-xs uppercase tracking-widest bg-muted/60 px-4 py-2">
            <div className="col-span-4">Product</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Barcode</div>
            <div className="col-span-2 text-right">Price</div>
            <div className="col-span-2 text-right">Stock</div>
          </div>
          {filtered.map((p) => {
            const ratio = Math.min(100, Math.round((p.stock / (p.reorder * 3)) * 100));
            const low = p.stock < p.reorder;
            return (
              <div key={p.id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-t border-border">
                <div className="col-span-4">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">SKU {p.sku}</div>
                </div>
                <div className="col-span-2"><Badge variant="secondary">{p.category}</Badge></div>
                <div className="col-span-2 font-mono text-xs flex items-center gap-1"><Barcode className="w-3 h-3"/> {p.barcode}</div>
                <div className="col-span-2 text-right">{formatCedi(p.price)}</div>
                <div className="col-span-2 text-right">
                  <div className={`text-sm font-semibold ${low ? "text-destructive" : ""}`}>{p.stock}</div>
                  <div className="mt-1"><Progress value={ratio}/></div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-primary">Best customers</div>
          <div className="font-display text-lg font-semibold mb-3">Top by spend</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={topCustomers}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11}/>
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11}/>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v) => formatCedi(v)}/>
                <Bar dataKey="spent" fill="#005BBB" radius={[8, 8, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent></Card>

        <Card className="glass"><CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-destructive">Low stock alerts</div>
          <div className="font-display text-lg font-semibold mb-3">Reorder recommended</div>
          {lowStock.length === 0 ? (
            <div className="text-sm text-muted-foreground">All good — no products below reorder point.</div>
          ) : (
            <div className="space-y-2">
              {lowStock.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">Reorder at {p.reorder} · Current {p.stock}</div>
                  </div>
                  <Badge variant="destructive">Order {p.reorder * 2 - p.stock} units</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      </div>

      <BeforeAfter demoId={5}/>
      <ConsultationCTA demoLabel="Inventory & Sales"/>
    </div>
  );
}
